import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Select, { StylesConfig, SingleValue } from 'react-select';
import { employeeApi, roleApi, locationApi, positionApi, payrollDepartmentApi, scheduleApi, paymentTypeApi, paymentFrequencyApi, payRecordApi, employeeLicenseApi, feedbackRecordApi, trainingRecordApi, employeeNoteApi } from '../services/api';
import { Employee, Role, CreateEmployeeRequest, Location, Position, PayrollDepartment, Schedule, PaymentType, PaymentFrequency, PayRecord, EmployeeLicense, FeedbackRecord, TrainingRecord, EmployeeNote } from '../types';
import { Plus, Edit, Trash2, Search, User, Filter, X, ChevronDown, ChevronUp, ArrowUp, ArrowDown } from 'lucide-react';
import BasketSelect from '../components/BasketSelect';
import { useLocationContext } from '../components/Layout';

interface FilterCondition {
  id: string;
  field: string;
  value: string | number | boolean | number[] | string[]; // Support arrays for multi-select
  valueFrom?: string | number | null; // For range filters (date/number)
  valueTo?: string | number | null; // For range filters (date/number)
}

type FilterFieldType = 'text' | 'date' | 'select' | 'boolean' | 'number' | 'dateRange' | 'numberRange' | 'multiSelect';

interface FilterFieldOption {
  value: string;
  label: string;
  type: FilterFieldType;
  tab: string;
  options?: Array<{ value: string | number; label: string }>;
}

const EmployeeList: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId } = useLocationContext();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [payrollDepartments, setPayrollDepartments] = useState<PayrollDepartment[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const [paymentFrequencies, setPaymentFrequencies] = useState<PaymentFrequency[]>([]);
  // Related data for filtering - fetched on-demand when filters are applied
  const [allPayRecords, setAllPayRecords] = useState<PayRecord[]>([]);
  const [allLicenses, setAllLicenses] = useState<EmployeeLicense[]>([]);
  const [allFeedbackRecords, setAllFeedbackRecords] = useState<FeedbackRecord[]>([]);
  const [allTrainingRecords, setAllTrainingRecords] = useState<TrainingRecord[]>([]);
  const [allEmployeeNotes, setAllEmployeeNotes] = useState<EmployeeNote[]>([]);
  const [relatedDataLoading, setRelatedDataLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Active' | 'InActive' | 'All'>('Active');
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]); // Store all employees before filtering
  const [isSearching, setIsSearching] = useState(false); // Track if related data fetch is in progress
  const searchLockRef = useRef(false); // Synchronous lock to prevent concurrent searches
  const activeApiCallsRef = useRef<Set<string>>(new Set()); // Track active API calls by type
  const abortControllerRef = useRef<AbortController | null>(null); // For cancelling API calls
  const searchCallCountRef = useRef(0); // Track how many times handleSearch is called
  const lastFiltersRef = useRef<string>(''); // Track last filters that triggered fetch to prevent loops
  const [sortColumn, setSortColumn] = useState<'name' | 'position' | 'location' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showSubLocationsDialog, setShowSubLocationsDialog] = useState(false);
  const [showSubPositionsDialog, setShowSubPositionsDialog] = useState(false);
  const [selectedEmployeeForSubLocations, setSelectedEmployeeForSubLocations] = useState<Employee | null>(null);
  const [selectedEmployeeForSubPositions, setSelectedEmployeeForSubPositions] = useState<Employee | null>(null);
  const [formData, setFormData] = useState<CreateEmployeeRequest>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    pin: '',
    appRoleIds: [],
    isActive: true,
    mainLocationId: 0,
    mainPositionId: 0,
    payrollDepartmentId: 0,
    employmentType: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Automatically fetch related data when filters that need it are applied
  useEffect(() => {
    // Skip if already searching to prevent infinite loops
    if (isSearching || searchLockRef.current) {
      return;
    }

    // Check if any filter requires related data
    // For numberRange/dateRange filters, check valueFrom/valueTo instead of value
    const needsPayRecords = filters.some(f => {
      if (!f.field || !f.field.startsWith('payRecord')) return false;
      // Check if filter has a value (for select/text) or valueFrom/valueTo (for range filters)
      return f.value || f.valueFrom || f.valueTo;
    });
    const needsLicenses = filters.some(f => f.field && f.field.startsWith('license') && (f.value || f.valueFrom || f.valueTo));
    const needsFeedback = filters.some(f => f.field && f.field.startsWith('feedback') && (f.value || f.valueFrom || f.valueTo));
    const needsTraining = filters.some(f => f.field && f.field.startsWith('training') && (f.value || f.valueFrom || f.valueTo));
    const needsNotes = filters.some(f => f.field && f.field.startsWith('notes') && (f.value || f.valueFrom || f.valueTo));

    // Create a stable key from filters to detect actual changes
    const filtersKey = JSON.stringify(filters.map(f => ({
      field: f.field,
      value: f.value,
      valueFrom: f.valueFrom,
      valueTo: f.valueTo
    })));

    // Only fetch if filters need related data AND filters have actually changed
    if ((needsPayRecords || needsLicenses || needsFeedback || needsTraining || needsNotes) && filtersKey !== lastFiltersRef.current) {
      lastFiltersRef.current = filtersKey;
      fetchRelatedDataIfNeeded();
    }
  }, [filters]);

  // Fetch related data automatically when filters that need it are applied
  const fetchRelatedDataIfNeeded = async () => {
    // Increment call counter
    searchCallCountRef.current += 1;
    const callNumber = searchCallCountRef.current;
    console.log(`[Search] handleSearch called #${callNumber} at ${new Date().toISOString()}`, new Error().stack);
    
    // CRITICAL: Use ref for immediate synchronous check - state updates are async
    if (searchLockRef.current) {
      console.log(`[Search] BLOCKED #${callNumber} - Search already in progress (ref check)`);
      return;
    }

    // Cancel any previous API calls
    if (abortControllerRef.current) {
      console.log('[Search] Cancelling previous API calls');
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this search
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // LOCK immediately - this is synchronous and prevents any other calls
    searchLockRef.current = true;
    const searchId = `search-${Date.now()}-${Math.random()}`;
    console.log('[Search] LOCKED - Starting search:', searchId);

    // Check if any filter requires related data
    // For numberRange/dateRange filters, check valueFrom/valueTo instead of value
    const needsPayRecords = filters.some(f => {
      if (!f.field || !f.field.startsWith('payRecord')) return false;
      // Check if filter has a value (for select/text) or valueFrom/valueTo (for range filters)
      return f.value || f.valueFrom || f.valueTo;
    });
    const needsLicenses = filters.some(f => f.field && f.field.startsWith('license') && (f.value || f.valueFrom || f.valueTo));
    const needsFeedback = filters.some(f => f.field && f.field.startsWith('feedback') && (f.value || f.valueFrom || f.valueTo));
    const needsTraining = filters.some(f => f.field && f.field.startsWith('training') && (f.value || f.valueFrom || f.valueTo));
    const needsNotes = filters.some(f => f.field && f.field.startsWith('notes') && (f.value || f.valueFrom || f.valueTo));

    // If no filters need related data, nothing to fetch
    if (!needsPayRecords && !needsLicenses && !needsFeedback && !needsTraining && !needsNotes) {
      searchLockRef.current = false;
      abortControllerRef.current = null;
      return;
    }

    // Check if data is already loaded - if so, skip API calls
    const hasPayRecordsData = !needsPayRecords || allPayRecords.length > 0;
    const hasLicensesData = !needsLicenses || allLicenses.length > 0;
    const hasFeedbackData = !needsFeedback || allFeedbackRecords.length > 0;
    const hasTrainingData = !needsTraining || allTrainingRecords.length > 0;
    const hasNotesData = !needsNotes || allEmployeeNotes.length > 0;
    
    if (hasPayRecordsData && hasLicensesData && hasFeedbackData && hasTrainingData && hasNotesData) {
      console.log(`[Search] Data already loaded, skipping API calls #${callNumber}`);
      searchLockRef.current = false;
      abortControllerRef.current = null;
      return;
    }

    // Set searching state
    setIsSearching(true);
    setRelatedDataLoading(true);
    
    try {
      // Create axios instance for other API calls (pay records uses payRecordApi)
      const { default: axios } = await import('axios');
      const getApiBaseUrl = () => {
        if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.API_URL) {
          return (window as any).APP_CONFIG.API_URL;
        }
        if (process.env.REACT_APP_API_URL) {
          return process.env.REACT_APP_API_URL;
        }
        return 'http://localhost:5026/api';
      };
      const baseURL = getApiBaseUrl();
      const authToken = localStorage.getItem('authToken');
      const authenticatedAxios = axios.create({
        baseURL: baseURL,
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` })
        }
      });

      // Make API calls sequentially, one at a time
      if (needsPayRecords && !abortController.signal.aborted) {
        // Double check - if data already exists, don't fetch again
        if (allPayRecords.length > 0) {
          console.log('[Search] Pay records already loaded, skipping API call', searchId);
        } else {
          console.log('[Search] Making SINGLE API call to payRecordApi.getAll()...', searchId, 'Call #', callNumber);
          const startTime = Date.now();
          try {
            // Use the configured payRecordApi which uses the proper api instance
            // IMPORTANT: If the server continuously streams data, this will hang until server completes
            const payRecordsData = await payRecordApi.getAll();
            const duration = Date.now() - startTime;
            console.log(`[Search] Pay records API call completed in ${duration}ms. Received ${Array.isArray(payRecordsData) ? payRecordsData.length : 0} records.`, searchId, 'Call #', callNumber);
            
            if (!abortController.signal.aborted) {
              setAllPayRecords(Array.isArray(payRecordsData) ? payRecordsData : []);
            }
          } catch (err: any) {
            const duration = Date.now() - startTime;
            if (abortController.signal.aborted) {
              console.log('[Search] Pay records fetch cancelled after', duration, 'ms', searchId);
            } else {
              console.error(`[Search] Error fetching pay records after ${duration}ms:`, err.message || err, 'Call #', callNumber);
            }
          }
        }
      }
      
      if (needsLicenses && !abortController.signal.aborted) {
        console.log('[Search] Making SINGLE API call to /employee-licenses...', searchId);
        try {
          const res = await authenticatedAxios.get('/employee-licenses', {
            signal: abortController.signal
          });
          const licensesData = Array.isArray(res.data) ? res.data : [];
          console.log('[Search] Licenses fetched successfully:', licensesData.length, searchId);
          if (!abortController.signal.aborted) {
            setAllLicenses(licensesData);
          }
        } catch (err: any) {
          if (err.name === 'CanceledError' || abortController.signal.aborted) {
            console.log('[Search] Licenses fetch cancelled', searchId);
          } else {
            console.error('[Search] Error fetching licenses:', err.message || err);
          }
        }
      }
      
      if (needsFeedback && !abortController.signal.aborted) {
        console.log('[Search] Making SINGLE API call to /feedback-records...', searchId);
        try {
          const res = await authenticatedAxios.get('/feedback-records', {
            signal: abortController.signal
          });
          const feedbackData = Array.isArray(res.data) ? res.data : [];
          console.log('[Search] Feedback records fetched successfully:', feedbackData.length, searchId);
          if (!abortController.signal.aborted) {
            setAllFeedbackRecords(feedbackData);
          }
        } catch (err: any) {
          if (err.name === 'CanceledError' || abortController.signal.aborted) {
            console.log('[Search] Feedback fetch cancelled', searchId);
          } else {
            console.error('[Search] Error fetching feedback:', err.message || err);
          }
        }
      }
      
      if (needsTraining && !abortController.signal.aborted) {
        console.log('[Search] Making SINGLE API call to /training-records...', searchId);
        try {
          const res = await authenticatedAxios.get('/training-records', {
            signal: abortController.signal
          });
          const trainingData = Array.isArray(res.data) ? res.data : [];
          console.log('[Search] Training records fetched successfully:', trainingData.length, searchId);
          if (!abortController.signal.aborted) {
            setAllTrainingRecords(trainingData);
          }
        } catch (err: any) {
          if (err.name === 'CanceledError' || abortController.signal.aborted) {
            console.log('[Search] Training fetch cancelled', searchId);
          } else {
            console.error('[Search] Error fetching training:', err.message || err);
          }
        }
      }
      
      if (needsNotes && !abortController.signal.aborted) {
        console.log('[Search] Making SINGLE API call to /employee-notes...', searchId);
        try {
          const res = await authenticatedAxios.get('/employee-notes', {
            signal: abortController.signal
          });
          const notesData = Array.isArray(res.data) ? res.data : [];
          console.log('[Search] Notes fetched successfully:', notesData.length, searchId);
          if (!abortController.signal.aborted) {
            setAllEmployeeNotes(notesData);
          }
        } catch (err: any) {
          if (err.name === 'CanceledError' || abortController.signal.aborted) {
            console.log('[Search] Notes fetch cancelled', searchId);
          } else {
            console.error('[Search] Error fetching notes:', err.message || err);
          }
        }
      }
      
      // Data fetched successfully
      if (!abortController.signal.aborted) {
        console.log('[Search] All data fetched successfully', searchId);
      }
      
    } catch (error: any) {
      if (error.name !== 'CanceledError') {
        console.error('[Search] Error fetching related data:', error);
      }
    } finally {
      // CRITICAL: Always unlock and cleanup, even on error
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
      setIsSearching(false);
      setRelatedDataLoading(false);
      searchLockRef.current = false; // UNLOCK - must be in finally block
      console.log('[Search] Search completed and UNLOCKED:', searchId);
    }
  };

  const fetchData = async () => {
    try {
      const [employeesData, rolesData, locationsData, positionsData, payrollDepartmentsData, schedulesData, paymentTypesData, paymentFrequenciesData] = await Promise.all([
        employeeApi.getAll(),
        roleApi.getAll(),
        locationApi.getAll(),
        positionApi.getAll(),
        payrollDepartmentApi.getAll(),
        scheduleApi.getAll(),
        paymentTypeApi.getAll(),
        paymentFrequencyApi.getAll()
      ]);
      
      setEmployees(employeesData);
      setAllEmployees(employeesData); // Store all employees for filtering
      setRoles(rolesData);
      setLocations(locationsData);
      setPositions(positionsData);
      setPayrollDepartments(payrollDepartmentsData);
      setSchedules(schedulesData);
      setPaymentTypes(paymentTypesData);
      setPaymentFrequencies(paymentFrequenciesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      pin: '',
      appRoleIds: [],
      isActive: true,
      mainLocationId: 0,
      mainPositionId: 0,
      payrollDepartmentId: 0,
      employmentType: ''
    });
    setConfirmPassword('');
    setError(null);
    setShowModal(true);
  };

  const handleEdit = (employee: Employee) => {
    // Navigate to employee profile page for editing
    navigate(`/employees/${employee.id}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      return;
    }
    if (formData.password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!formData.pin || formData.pin.length !== 4) {
      setError('PIN must be exactly 4 digits');
      return;
    }
    if (!formData.appRoleIds || formData.appRoleIds.length === 0) {
      setError('At least one app role is required');
      return;
    }
    if (!formData.mainLocationId || formData.mainLocationId === 0) {
      setError('Main Location is required');
      return;
    }
    if (!formData.mainPositionId || formData.mainPositionId === 0) {
      setError('Main Position is required');
      return;
    }
    if (!formData.payrollDepartmentId || formData.payrollDepartmentId === 0) {
      setError('Payroll Department is required');
      return;
    }
    if (!formData.employmentType || formData.employmentType.trim() === '') {
      setError('Employment Type is required');
      return;
    }

    try {
      const newEmployee = await employeeApi.create(formData);
      setShowModal(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        pin: '',
        appRoleIds: [],
        isActive: true,
        mainLocationId: 0,
        mainPositionId: 0,
        payrollDepartmentId: 0,
        employmentType: ''
      });
      setConfirmPassword('');
      // Navigate to the new employee's profile page
      navigate(`/employees/${newEmployee.id}?tab=personal`);
    } catch (error: any) {
      console.error('Error creating employee:', error);
      setError(error?.response?.data?.message || 'Failed to create employee. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await employeeApi.delete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting employee:', error);
        alert('Failed to delete employee. Please try again.');
      }
    }
  };

  // Define filter field options
  const filterFields: FilterFieldOption[] = useMemo(() => {
    const fields: FilterFieldOption[] = [
      // Login tab fields - Only Email and PIN
      { value: 'email', label: 'Email', type: 'text', tab: 'Login' },
      { value: 'pin', label: 'PIN', type: 'text', tab: 'Login' },
      
      // Personal tab fields
      { value: 'firstName', label: 'First Name', type: 'text', tab: 'Personal' },
      { value: 'lastName', label: 'Last Name', type: 'text', tab: 'Personal' },
      { value: 'gender', label: 'Gender', type: 'select', tab: 'Personal', options: [
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' },
        { value: 'Prefer Not to Say', label: 'Prefer Not to Say' }
      ]},
      { value: 'genderOther', label: 'Gender (Other)', type: 'text', tab: 'Personal' },
      { value: 'birthday', label: 'Birthday', type: 'dateRange', tab: 'Personal' },
      { value: 'phone', label: 'Phone', type: 'text', tab: 'Personal' },
      { value: 'address', label: 'Address', type: 'text', tab: 'Personal' },
      { value: 'psaLicense', label: 'PSA License', type: 'text', tab: 'Personal' },
      { value: 'startDate', label: 'Start Date', type: 'dateRange', tab: 'Personal' },
      { value: 'visaNumber', label: 'Visa Number', type: 'text', tab: 'Personal' },
      { value: 'visaExpiryDate', label: 'Visa Expiry Date', type: 'dateRange', tab: 'Personal' },
      { value: 'comment', label: 'Comment', type: 'text', tab: 'Personal' },
      
      // Work tab fields
      { value: 'mainLocationId', label: 'Main Location', type: 'select', tab: 'Work', options: locations.map(loc => ({ value: loc.id, label: loc.name })) },
      { value: 'mainOrSubLocationIds', label: 'Main or Sub Locations', type: 'multiSelect', tab: 'Work', options: locations.map(loc => ({ value: loc.id, label: loc.name })) },
      { value: 'mainPositionId', label: 'Main Position', type: 'select', tab: 'Work', options: positions.map(pos => ({ value: pos.id, label: pos.title })) },
      { value: 'mainOrSubPositionIds', label: 'Main or Sub Positions', type: 'multiSelect', tab: 'Work', options: positions.map(pos => ({ value: pos.id, label: pos.title })) },
      { value: 'scheduleIds', label: 'Schedules', type: 'multiSelect', tab: 'Work', options: schedules.map(sch => ({ value: sch.id, label: sch.name })) },
      { value: 'appRoleIds', label: 'App Roles', type: 'multiSelect', tab: 'Work', options: roles.map(role => ({ value: role.id, label: role.name })) },
      { value: 'payrollDepartmentId', label: 'Payroll Department', type: 'select', tab: 'Work', options: payrollDepartments.filter(dept => dept.isActive).map(dept => ({ value: dept.id, label: dept.name })) },
      { value: 'employmentType', label: 'Employment Type', type: 'select', tab: 'Work', options: [
        { value: 'Permanent', label: 'Permanent' },
        { value: 'Contract', label: 'Contract' },
        { value: 'Agency', label: 'Agency' }
      ]},
      { value: 'allowMobileClockIn', label: 'Allow Mobile Clock In', type: 'boolean', tab: 'Work' },
      
      // License fields
      { value: 'licenseNumber', label: 'License - Number', type: 'text', tab: 'License' },
      { value: 'licenseExpiryDate', label: 'License - Expiry Date', type: 'dateRange', tab: 'License' },
      
      // Payroll fields
      { value: 'worksNumber', label: 'Works Number', type: 'text', tab: 'Payroll' },
      { value: 'ppsNumber', label: 'PPS Number', type: 'text', tab: 'Payroll' },
      
      // Pay Record fields (only active records)
      { value: 'payRecordPaymentTypeId', label: 'Pay Type', type: 'select', tab: 'Pay Record', options: paymentTypes.filter(pt => pt.isActive).map(pt => ({ value: pt.id, label: pt.name })) },
      { value: 'payRecordPaymentFrequencyId', label: 'Pay Frequency', type: 'select', tab: 'Pay Record', options: paymentFrequencies.filter(pf => pf.isActive).map(pf => ({ value: pf.id, label: pf.name })) },
      { value: 'payRecordContractedHours', label: 'Contracted Hours (Per Week)', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordBaseRate', label: 'Pay Rate', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordSundayRateMultiplier', label: 'Sunday Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordBankHolidayRateMultiplier', label: 'Bank Holiday Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordNightShiftMultiplier', label: 'Night Shift Multiplier', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordOvertimeEligible', label: 'Is Overtime Eligible', type: 'boolean', tab: 'Pay Record' },
      { value: 'payRecordOt1TriggerHours', label: 'OT1 Trigger Hours', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordOt1RateMultiplier', label: 'OT1 Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordOt2TriggerHours', label: 'OT2 Trigger Hours', type: 'numberRange', tab: 'Pay Record' },
      { value: 'payRecordOt2RateMultiplier', label: 'OT2 Rate Multiplier', type: 'numberRange', tab: 'Pay Record' },
      
      // Feedback fields
      { value: 'feedbackType', label: 'Feedback - Type', type: 'text', tab: 'Feedback' },
      { value: 'feedbackActionTaken', label: 'Feedback - Action Taken', type: 'text', tab: 'Feedback' },
      { value: 'feedbackDescription', label: 'Feedback - Description', type: 'text', tab: 'Feedback' },
      { value: 'feedbackPriority', label: 'Feedback - Priority', type: 'text', tab: 'Feedback' },
      { value: 'feedbackStatus', label: 'Feedback - Status', type: 'text', tab: 'Feedback' },
      
      // Training fields
      { value: 'trainingUnassigned', label: 'Training - Unassigned', type: 'boolean', tab: 'Training' },
      { value: 'trainingPending', label: 'Training - Pending', type: 'boolean', tab: 'Training' },
      { value: 'trainingCompleted', label: 'Training - Completed', type: 'boolean', tab: 'Training' },
      
      // Notes fields
      { value: 'notesSubject', label: 'Notes - Subject', type: 'text', tab: 'Notes' },
      { value: 'notesContent', label: 'Notes - Description', type: 'text', tab: 'Notes' },
    ];
    return fields;
  }, [locations, positions, payrollDepartments, schedules, roles, paymentTypes, paymentFrequencies]);

  // Add a new filter condition
  const handleAddFilter = () => {
    const newFilter: FilterCondition = {
      id: Date.now().toString(),
      field: '',
      value: ''
    };
    setFilters([...filters, newFilter]);
  };

  // Remove a filter condition
  const handleRemoveFilter = (id: string) => {
    setFilters(filters.filter(f => f.id !== id));
  };

  // Update a filter condition
  const handleFilterChange = (id: string, field: keyof FilterCondition, value: string | number | boolean | number[] | string[] | null | undefined) => {
    setFilters(prevFilters => prevFilters.map(f => {
      if (f.id === id) {
        return { ...f, [field]: value };
      }
      return f;
    }));
  };

  // Update filter field and reset value
  const handleFilterFieldChange = (id: string, fieldValue: string) => {
    setFilters(prevFilters => prevFilters.map(f => {
      if (f.id === id) {
        return { ...f, field: fieldValue, value: '', valueFrom: undefined, valueTo: undefined };
      }
      return f;
    }));
  };

  // Clear all filters and reset to show all employees
  const handleClearFilters = () => {
    setFilters([]);
    setEmployees(allEmployees); // Reset to all employees
    // Clear related data
    setAllPayRecords([]);
    setAllLicenses([]);
    setAllFeedbackRecords([]);
    setAllTrainingRecords([]);
    setAllEmployeeNotes([]);
  };

  // Handle sub-locations dialog
  const handleShowSubLocations = (employee: Employee) => {
    setSelectedEmployeeForSubLocations(employee);
    setShowSubLocationsDialog(true);
  };

  // Handle sub-positions dialog
  const handleShowSubPositions = (employee: Employee) => {
    setSelectedEmployeeForSubPositions(employee);
    setShowSubPositionsDialog(true);
  };

  // Get sub-locations for an employee
  const getSubLocations = (employee: Employee): Location[] => {
    if (!employee.profile?.subLocationIds || !Array.isArray(employee.profile.subLocationIds)) {
      return [];
    }
    return locations.filter(loc => employee.profile?.subLocationIds?.includes(loc.id));
  };

  // Handle column sorting
  const handleSort = (column: 'name' | 'position' | 'location') => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sub-positions for an employee
  const getSubPositions = (employee: Employee): Position[] => {
    // Check if subPositionIds exists and is an array
    const subPositionIds = employee.profile?.subPositionIds;
    
    // Handle different data formats (array, string array, etc.)
    let positionIds: number[] = [];
    
    if (Array.isArray(subPositionIds)) {
      // Convert to numbers if they're strings
      positionIds = subPositionIds.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id));
    } else if (subPositionIds) {
      // If it's a single value or string, try to parse it
      const parsed = typeof subPositionIds === 'string' ? JSON.parse(subPositionIds) : subPositionIds;
      if (Array.isArray(parsed)) {
        positionIds = parsed.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id));
      }
    }
    
    if (positionIds.length === 0) {
      return [];
    }
    
    return positions.filter(pos => positionIds.includes(pos.id));
  };

  // Get field type for a filter field
  const getFieldType = (fieldValue: string): FilterFieldType => {
    const field = filterFields.find(f => f.value === fieldValue);
    return field?.type || 'text';
  };

  // Get field options for a filter field
  const getFieldOptions = (fieldValue: string) => {
    const field = filterFields.find(f => f.value === fieldValue);
    return field?.options || [];
  };

  // Filter employees - Location filter from top banner first, then status filter, then advanced filters, then search
  const filteredEmployees = useMemo(() => {
    // Use allEmployees as base - only filter if search has been performed or filters are applied
    let filtered = [...allEmployees];

    // STEP 1: Apply location filter from top banner FIRST (if selected)
    if (selectedLocationId !== null && selectedLocationId !== 0) {
      filtered = filtered.filter((employee) => {
        // Check if employee has the selected location (main location OR sub location)
        const hasMainLocation = employee.profile?.mainLocationId === selectedLocationId;
        const hasSubLocation = employee.profile?.subLocationIds && 
                              Array.isArray(employee.profile.subLocationIds) && 
                              employee.profile.subLocationIds.includes(selectedLocationId);
        return hasMainLocation || hasSubLocation;
      });
    }

    // STEP 2: Apply status filter (Active/InActive/All)
    if (statusFilter !== 'All') {
      filtered = filtered.filter((employee) => {
        if (statusFilter === 'Active') {
          return employee.isActive === true;
        } else if (statusFilter === 'InActive') {
          return employee.isActive === false;
        }
        return true;
      });
    }

    // STEP 3: Apply advanced filters (after location and status filters)
    
    filters.forEach(filter => {
      if (!filter.field) {
        return;
      }

      const fieldType = getFieldType(filter.field);
      
      // Skip if filter is not properly set based on field type
      if (fieldType === 'dateRange' || fieldType === 'numberRange') {
        if (!filter.valueFrom && !filter.valueTo) {
          return; // Skip if both from and to are empty
        }
      } else if (fieldType === 'multiSelect') {
        if (!Array.isArray(filter.value) || filter.value.length === 0) {
          return; // Skip if no values selected
        }
      } else {
        if (filter.value === '' || filter.value === null || filter.value === undefined) {
          return; // Skip if value is empty
        }
      }

      filtered = filtered.filter((employee) => {
        // Handle related data filters (Pay Record, License, Feedback, Training, Notes)
        if (filter.field.startsWith('payRecord')) {
          // Early return if no pay records data loaded
          if (!allPayRecords || allPayRecords.length === 0) {
            return false; // No pay records data, so no matches
          }
          
          // Get ONLY ACTIVE pay records for this employee
          // IMPORTANT: Ignore all inactive pay records when filtering
          const employeePayRecords = allPayRecords.filter(pr => {
            if (!pr) return false;
            
            // Must match employee ID (handle both number and string IDs)
            const prEmployeeId = typeof pr.employeeId === 'string' ? Number(pr.employeeId) : pr.employeeId;
            const empId = typeof employee.id === 'string' ? Number(employee.id) : employee.id;
            
            if (isNaN(prEmployeeId) || isNaN(empId) || prEmployeeId !== empId) {
              return false;
            }
            
            // STRICT CHECK: Only include records where isActive is explicitly true
            // Reject all inactive records (false, null, undefined, 0, 'false', etc.)
            if (pr.isActive !== true) {
              return false; // Ignore inactive records
            }
            
            return true;
          });
          
          console.log(`[Filter] Employee ${employee.id} (${employee.firstName} ${employee.lastName}): Found ${employeePayRecords.length} active pay records`);
          if (employeePayRecords.length === 0) {
            console.log(`[Filter] No active pay records for employee ${employee.id}, excluding`);
            return false;
          }
          
          // Log first pay record for debugging BaseRate filter
          if (employeePayRecords.length > 0 && filter.field === 'payRecordBaseRate') {
            console.log('[Filter] Sample pay record for BaseRate filter:', {
              id: employeePayRecords[0].id,
              employeeId: employeePayRecords[0].employeeId,
              baseRate: employeePayRecords[0].baseRate,
              baseRateType: typeof employeePayRecords[0].baseRate,
              isActive: employeePayRecords[0].isActive,
              availableFields: Object.keys(employeePayRecords[0])
            });
          }
          
          // Check if any pay record matches the filter
          return employeePayRecords.some(pr => {
            if (filter.field === 'payRecordPaymentTypeId') {
              // Ensure both values are numbers for comparison
              const filterValue = typeof filter.value === 'number' ? filter.value : Number(filter.value);
              if (isNaN(filterValue)) return false;
              
              const prPaymentTypeId = typeof pr.paymentTypeId === 'number' ? pr.paymentTypeId : Number(pr.paymentTypeId);
              if (isNaN(prPaymentTypeId)) return false;
              
              return prPaymentTypeId === filterValue;
            } else if (filter.field === 'payRecordPaymentFrequencyId') {
              // Ensure both values are numbers for comparison
              const filterValue = typeof filter.value === 'number' ? filter.value : Number(filter.value);
              if (isNaN(filterValue)) return false;
              
              // Handle null/undefined paymentFrequencyId - if filter is set, record must have it
              if (pr.paymentFrequencyId === null || pr.paymentFrequencyId === undefined) {
                return false;
              }
              
              const prPaymentFrequencyId = typeof pr.paymentFrequencyId === 'number' ? pr.paymentFrequencyId : Number(pr.paymentFrequencyId);
              if (isNaN(prPaymentFrequencyId)) return false;
              
              return prPaymentFrequencyId === filterValue;
            } else if (filter.field === 'payRecordContractedHours') {
              const value = pr.contractedHours;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordBaseRate') {
              const value = pr.baseRate;
              // Debug logging
              console.log('[Filter] PayRecord BaseRate filter:', {
                payRecordId: pr.id,
                employeeId: pr.employeeId,
                baseRate: value,
                baseRateType: typeof value,
                filterFrom: filter.valueFrom,
                filterTo: filter.valueTo
              });
              
              if (value === null || value === undefined) {
                console.log('[Filter] PayRecord BaseRate is null/undefined, excluding');
                return false;
              }
              
              // Handle empty strings and null/undefined - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '' && !isNaN(Number(filter.valueFrom))) 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '' && !isNaN(Number(filter.valueTo))) 
                ? Number(filter.valueTo) 
                : null;
              
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) {
                console.log('[Filter] Both from and to are null, including record');
                return true;
              }
              
              // Convert value to number for comparison
              const numValue = Number(value);
              if (isNaN(numValue)) {
                console.log('[Filter] BaseRate value is NaN, excluding');
                return false;
              }
              
              // Apply range filter
              console.log('[Filter] Comparing:', { numValue, from, to });
              if (from !== null && numValue < from) {
                console.log('[Filter] numValue < from, excluding');
                return false;
              }
              if (to !== null && numValue > to) {
                console.log('[Filter] numValue > to, excluding');
                return false;
              }
              
              console.log('[Filter] PayRecord BaseRate matches filter, including');
              return true;
            } else if (filter.field === 'payRecordSundayRateMultiplier') {
              const value = pr.sundayRateMultiplier ?? 1.0;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordBankHolidayRateMultiplier') {
              const value = pr.bankHolidayRateMultiplier ?? 1.0;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordNightShiftMultiplier') {
              const value = pr.nightShiftMultiplier ?? 1.0;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordOvertimeEligible') {
              return pr.overtimeEligible === filter.value;
            } else if (filter.field === 'payRecordOt1TriggerHours') {
              const value = pr.ot1TriggerHours;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordOt1RateMultiplier') {
              const value = pr.ot1RateMultiplier;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordOt2TriggerHours') {
              const value = pr.ot2TriggerHours;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            } else if (filter.field === 'payRecordOt2RateMultiplier') {
              const value = pr.ot2RateMultiplier;
              if (value === null || value === undefined) return false;
              // Handle empty strings - treat them as null (no filter)
              const from = (filter.valueFrom !== null && filter.valueFrom !== undefined && filter.valueFrom !== '') 
                ? Number(filter.valueFrom) 
                : null;
              const to = (filter.valueTo !== null && filter.valueTo !== undefined && filter.valueTo !== '') 
                ? Number(filter.valueTo) 
                : null;
              // If both from and to are null, no range filter is applied - return true
              if (from === null && to === null) return true;
              if (from !== null && !isNaN(from) && value < from) return false;
              if (to !== null && !isNaN(to) && value > to) return false;
              return true;
            }
            return false;
          });
        } else if (filter.field.startsWith('license')) {
          const employeeLicenses = allLicenses.filter(lic => lic.employeeId === employee.id);
          
          if (filter.field === 'licenseNumber') {
            if (employeeLicenses.length === 0) return false;
            const searchStr = String(filter.value || '').toLowerCase();
            return employeeLicenses.some(lic => 
              String(lic.licenseNumber || '').toLowerCase().includes(searchStr)
            );
          } else if (filter.field === 'licenseExpiryDate') {
            if (employeeLicenses.length === 0) return false;
            return employeeLicenses.some(lic => {
              if (!lic.expiryDate) return false;
              const expiryDate = new Date(lic.expiryDate).toISOString().split('T')[0];
              const from = filter.valueFrom ? String(filter.valueFrom) : null;
              const to = filter.valueTo ? String(filter.valueTo) : null;
              if (from && expiryDate < from) return false;
              if (to && expiryDate > to) return false;
              return true;
            });
          }
          return false;
        } else if (filter.field.startsWith('worksNumber') || filter.field.startsWith('ppsNumber')) {
          // These are payroll fields - check employee payroll
          const value = filter.field === 'worksNumber' 
            ? employee.payroll?.worksNumber 
            : employee.payroll?.ppsNumber;
          if (!value) return false;
          const searchStr = String(filter.value || '').toLowerCase();
          return String(value).toLowerCase().includes(searchStr);
        } else if (filter.field.startsWith('feedback')) {
          const employeeFeedback = allFeedbackRecords.filter(fb => fb.employeeId === employee.id);
          if (employeeFeedback.length === 0) return false;
          
          const searchStr = String(filter.value || '').toLowerCase();
          return employeeFeedback.some(fb => {
            if (filter.field === 'feedbackType') {
              return String(fb.type || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'feedbackActionTaken') {
              return String(fb.actionTaken || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'feedbackDescription') {
              return String(fb.description || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'feedbackPriority') {
              return String(fb.priority || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'feedbackStatus') {
              return String(fb.status || '').toLowerCase().includes(searchStr);
            }
            return false;
          });
        } else if (filter.field.startsWith('training')) {
          const employeeTraining = allTrainingRecords.filter(tr => tr.employeeId === employee.id);
          
          if (filter.field === 'trainingUnassigned') {
            return employeeTraining.some(tr => tr.status === 'Unassigned') === filter.value;
          } else if (filter.field === 'trainingPending') {
            return employeeTraining.some(tr => tr.status === 'Pending') === filter.value;
          } else if (filter.field === 'trainingCompleted') {
            return employeeTraining.some(tr => tr.status === 'Completed') === filter.value;
          }
          return false;
        } else if (filter.field.startsWith('notes')) {
          const employeeNotes = allEmployeeNotes.filter(note => note.employeeId === employee.id);
          if (employeeNotes.length === 0) return false;
          
          const searchStr = String(filter.value || '').toLowerCase();
          return employeeNotes.some(note => {
            if (filter.field === 'notesSubject') {
              return String(note.subject || '').toLowerCase().includes(searchStr);
            } else if (filter.field === 'notesContent') {
              return String(note.content || '').toLowerCase().includes(searchStr);
            }
            return false;
          });
        } else if (filter.field === 'mainOrSubLocationIds') {
          // Multi-select: check if employee has any of the selected locations
          if (!Array.isArray(filter.value) || filter.value.length === 0) return false;
          const selectedIds = filter.value.map(v => Number(v));
          const hasMainLocation = employee.profile?.mainLocationId && selectedIds.includes(employee.profile.mainLocationId);
          const hasSubLocation = employee.profile?.subLocationIds && 
            Array.isArray(employee.profile.subLocationIds) &&
            employee.profile.subLocationIds.some((id: number) => selectedIds.includes(id));
          return hasMainLocation || hasSubLocation;
        } else if (filter.field === 'mainOrSubPositionIds') {
          // Multi-select: check if employee has any of the selected positions
          if (!Array.isArray(filter.value) || filter.value.length === 0) return false;
          const selectedIds = filter.value.map(v => Number(v));
          const hasMainPosition = employee.profile?.mainPositionId && selectedIds.includes(employee.profile.mainPositionId);
          const subPositionIds = employee.profile?.subPositionIds;
          let hasSubPosition = false;
          if (Array.isArray(subPositionIds)) {
            hasSubPosition = subPositionIds.some((id: number) => selectedIds.includes(id));
          }
          return hasMainPosition || hasSubPosition;
        } else if (filter.field === 'scheduleIds') {
          // Multi-select: check if employee has any of the selected schedules
          if (!Array.isArray(filter.value) || filter.value.length === 0) return false;
          const selectedIds = filter.value.map(v => Number(v));
          const employeeSchedules = employee.profile?.scheduleIds || [];
          return Array.isArray(employeeSchedules) && 
            employeeSchedules.some((id: number) => selectedIds.includes(id));
        } else if (filter.field === 'appRoleIds') {
          // Multi-select: check if employee has any of the selected app roles
          if (!Array.isArray(filter.value) || filter.value.length === 0) return false;
          const selectedIds = filter.value.map(v => Number(v));
          const employeeRoleIds = employee.profile?.appRoleIds || [];
          return Array.isArray(employeeRoleIds) && 
            employeeRoleIds.some((id: number) => selectedIds.includes(id));
        } else {
          // Standard employee fields - get value from employee object or profile
          let fieldValue: any;

          // Get the actual value from employee object or profile
          if (filter.field === 'mainLocationId') {
            fieldValue = employee.profile?.mainLocationId;
          } else if (filter.field === 'mainPositionId') {
            fieldValue = employee.profile?.mainPositionId;
          } else if (filter.field === 'payrollDepartmentId') {
            fieldValue = employee.profile?.payrollDepartmentId;
          } else if (filter.field === 'employmentType') {
            fieldValue = employee.profile?.employmentType;
          } else if (filter.field === 'allowMobileClockIn') {
            fieldValue = employee.profile?.allowMobileClockIn;
          } else {
            // Try to get from employee object directly
            // Fields like firstName, lastName, email, phone, gender, address, etc. are on employee
            fieldValue = (employee as any)[filter.field];
            
            // If not found and field might be in profile, try profile
            if (fieldValue === undefined && employee.profile) {
              fieldValue = (employee.profile as any)[filter.field];
            }
          }

          // Handle different field types
          if (fieldType === 'boolean') {
            return fieldValue === filter.value;
          } else if (fieldType === 'dateRange') {
            if (!fieldValue) return false;
            try {
              const fieldDate = new Date(fieldValue).toISOString().split('T')[0];
              if (isNaN(new Date(fieldDate).getTime())) return false;
              const from = filter.valueFrom ? String(filter.valueFrom) : null;
              const to = filter.valueTo ? String(filter.valueTo) : null;
              if (from && fieldDate < from) return false;
              if (to && fieldDate > to) return false;
              return true;
            } catch (e) {
              return false;
            }
          } else if (fieldType === 'numberRange') {
            const numValue = Number(fieldValue);
            if (isNaN(numValue) || fieldValue === null || fieldValue === undefined) return false;
            const from = filter.valueFrom ? Number(filter.valueFrom) : null;
            const to = filter.valueTo ? Number(filter.valueTo) : null;
            if (from !== null && numValue < from) return false;
            if (to !== null && numValue > to) return false;
            return true;
          } else if (fieldType === 'select' || fieldType === 'number') {
            // Handle null/undefined - if field is null/undefined and filter has value, no match
            if ((fieldValue === null || fieldValue === undefined) && filter.value !== null && filter.value !== undefined) {
              return false;
            }
            // If filter is null/undefined, skip this filter (shouldn't happen, but be safe)
            if (filter.value === null || filter.value === undefined) {
              return true;
            }
            // For select fields, compare both as numbers if possible, otherwise as strings
            const filterVal = typeof filter.value === 'number' ? filter.value : Number(filter.value);
            const fieldVal = typeof fieldValue === 'number' ? fieldValue : Number(fieldValue);
            if (!isNaN(filterVal) && !isNaN(fieldVal)) {
              return fieldVal === filterVal;
            }
            // Fallback to string comparison
            return String(fieldValue || '') === String(filter.value || '');
          } else {
            // Text search (contains) - handle null/undefined properly
            const fieldStr = fieldValue !== null && fieldValue !== undefined 
              ? String(fieldValue).toLowerCase().trim() 
              : '';
            const filterStr = String(filter.value || '').toLowerCase().trim();
            
            // If filter is empty, skip this filter (shouldn't happen due to validation, but be safe)
            if (!filterStr) return true;
            
            // If field is empty/null and filter has value, no match
            if (!fieldStr) return false;
            
            // Perform contains search
            return fieldStr.includes(filterStr);
          }
        }
      });
    });

    // STEP 4: Apply search filter LAST (after location, status, and advanced filters)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((employee) => {
        return (
          employee.firstName?.toLowerCase().includes(searchLower) ||
          employee.lastName?.toLowerCase().includes(searchLower) ||
          employee.email?.toLowerCase().includes(searchLower)
        );
      });
    }

    // STEP 5: Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue: string = '';
        let bValue: string = '';

        if (sortColumn === 'name') {
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
        } else if (sortColumn === 'position') {
          aValue = a.profile?.mainPosition?.title || '';
          bValue = b.profile?.mainPosition?.title || '';
        } else if (sortColumn === 'location') {
          aValue = a.profile?.mainLocation?.name || a.profile?.locationName || '';
          bValue = b.profile?.mainLocation?.name || b.profile?.locationName || '';
        }

        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [allEmployees, selectedLocationId, statusFilter, filters, searchTerm, filterFields, sortColumn, sortDirection, allPayRecords, allLicenses, allFeedbackRecords, allTrainingRecords, allEmployeeNotes, locations, positions]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[82%] mx-auto p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold text-neutral-800">Employees</h1>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-neutral-600">
              Showing <span className="font-semibold text-neutral-800">{filteredEmployees?.length || 0}</span> of <span className="font-semibold text-neutral-800">{employees?.length || 0}</span> {employees?.length === 1 ? 'employee' : 'employees'}
              {statusFilter === 'Active' && (
                <span className="text-neutral-500 ml-1">
                  (Active only)
                </span>
              )}
              {statusFilter === 'InActive' && (
                <span className="text-neutral-500 ml-1">
                  (Inactive only)
                </span>
              )}
            </span>
            {(selectedLocationId || statusFilter !== 'Active' || searchTerm || filters.length > 0) && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                Filtered
              </span>
            )}
          </div>
        </div>
        <button
          onClick={handleAddNew}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
        >
          <Plus className="h-4 w-4" />
          Add New Employee
        </button>
      </div>

      {/* Search Box */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Search employees by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Advanced Filter Section and Status Filter */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <button
            onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors w-full sm:w-auto"
          >
            <Filter className="h-4 w-4" />
            Advanced Filters
            {filters.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                {filters.length}
              </span>
            )}
            {showAdvancedFilter ? (
              <ChevronUp className="h-4 w-4 ml-auto" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-auto" />
            )}
          </button>

          {/* Status Filter Dropdown */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-sm font-medium text-neutral-700 whitespace-nowrap">
              Status:
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'Active' | 'InActive' | 'All')}
              className="px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
            >
              <option value="Active">Active</option>
              <option value="InActive">InActive</option>
              <option value="All">All</option>
            </select>
          </div>
        </div>

        {/* Advanced Filter Content */}
        {showAdvancedFilter && (
          <div className="bg-white border border-neutral-200 rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-neutral-800">Filter Employees</h3>
              <div className="flex gap-2">
                {filters.length > 0 && (
                  <>
                    <button
                      onClick={handleClearFilters}
                      className="text-xs text-neutral-600 hover:text-neutral-800 px-2 py-1"
                    >
                      Clear All
                    </button>
                  </>
                )}
                <button
                  onClick={handleAddFilter}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100"
                >
                  <Plus className="h-3 w-3" />
                  Add Filter
                </button>
              </div>
            </div>

            {filters.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-4">
                No filters applied. Click "Add Filter" to start filtering employees.
              </p>
            ) : (
              <div className="space-y-3">
                {filters.map((filter) => {
                  const fieldType = getFieldType(filter.field);
                  const fieldOptions = getFieldOptions(filter.field);
                  const selectedField = filterFields.find(f => f.value === filter.field);

                  // Convert filterFields to react-select options
                  type FieldSelectOption = { value: string; label: string };
                  const fieldSelectOptions: FieldSelectOption[] = filterFields.map(field => ({
                    value: field.value,
                    label: field.label
                  }));

                  const selectedFieldOption: FieldSelectOption | null = filter.field 
                    ? fieldSelectOptions.find(opt => opt.value === filter.field) || null
                    : null;

                  // Custom styles for react-select (similar to BasketSelect but for single select)
                  const fieldSelectStyles: StylesConfig<FieldSelectOption, false> = {
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
                      },
                      minHeight: '38px',
                      fontSize: '14px',
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: '#9ca3af',
                    }),
                    input: (provided) => ({
                      ...provided,
                      color: '#374151',
                    }),
                  };

                  return (
                    <div key={filter.id} className="flex flex-col sm:flex-row gap-2 p-3 bg-neutral-50 rounded-md">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-neutral-700 mb-1">
                          Field
                        </label>
                        <Select<FieldSelectOption>
                          options={fieldSelectOptions}
                          value={selectedFieldOption}
                          onChange={(option: SingleValue<FieldSelectOption>) => {
                            handleFilterFieldChange(filter.id, option ? option.value : '');
                          }}
                          placeholder="Select Field..."
                          styles={fieldSelectStyles}
                          isSearchable
                          isClearable
                          className="react-select-container"
                          classNamePrefix="react-select"
                          noOptionsMessage={({ inputValue }) => 
                            inputValue ? `No fields found for "${inputValue}"` : 'No fields available'
                          }
                        />
                      </div>

                      <div className="flex-1">
                        {fieldType !== 'dateRange' && fieldType !== 'numberRange' && (
                          <label className="block text-xs font-medium text-neutral-700 mb-1">
                            Value
                          </label>
                        )}
                        {fieldType === 'boolean' ? (
                          <select
                            value={String(filter.value || '')}
                            onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value === 'true')}
                            className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        ) : fieldType === 'dateRange' ? (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-neutral-700 mb-1">From</label>
                              <input
                                type="date"
                                value={String(filter.valueFrom || '')}
                                onChange={(e) => handleFilterChange(filter.id, 'valueFrom', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-neutral-700 mb-1">To</label>
                              <input
                                type="date"
                                value={String(filter.valueTo || '')}
                                onChange={(e) => handleFilterChange(filter.id, 'valueTo', e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        ) : fieldType === 'numberRange' ? (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-neutral-700 mb-1">From</label>
                              <input
                                type="number"
                                step="0.01"
                                value={filter.valueFrom !== null && filter.valueFrom !== undefined ? filter.valueFrom : ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  handleFilterChange(filter.id, 'valueFrom', val === '' ? null : parseFloat(val));
                                }}
                                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-neutral-700 mb-1">To</label>
                              <input
                                type="number"
                                step="0.01"
                                value={filter.valueTo !== null && filter.valueTo !== undefined ? filter.valueTo : ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  handleFilterChange(filter.id, 'valueTo', val === '' ? null : parseFloat(val));
                                }}
                                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        ) : fieldType === 'multiSelect' && fieldOptions.length > 0 ? (
                          <BasketSelect
                            label=""
                            availableItems={fieldOptions.map(opt => ({ id: opt.value, label: opt.label }))}
                            selectedIds={(() => {
                              if (Array.isArray(filter.value)) {
                                // Handle array case - filter out booleans and keep only strings/numbers
                                const arr = filter.value as (string | number | boolean)[];
                                return arr.filter((v): v is string | number => 
                                  typeof v === 'string' || typeof v === 'number'
                                );
                              }
                              if (filter.value && (typeof filter.value === 'string' || typeof filter.value === 'number')) {
                                return [filter.value];
                              }
                              return [];
                            })()}
                            onChange={(selectedIds) => {
                              // Preserve the original type from options (number or string)
                              // Check if first option value is a number to determine type
                              const isNumeric = fieldOptions.length > 0 && typeof fieldOptions[0].value === 'number';
                              
                              if (isNumeric) {
                                const numValues: number[] = selectedIds.map(id => {
                                  const option = fieldOptions.find(opt => String(opt.value) === String(id));
                                  return option && typeof option.value === 'number' ? option.value : Number(id);
                                });
                                handleFilterChange(filter.id, 'value', numValues);
                              } else {
                                const strValues: string[] = selectedIds.map(id => {
                                  const option = fieldOptions.find(opt => String(opt.value) === String(id));
                                  return option ? String(option.value) : String(id);
                                });
                                handleFilterChange(filter.id, 'value', strValues);
                              }
                            }}
                            placeholder={`Select ${selectedField?.label.toLowerCase() || 'options'}...`}
                          />
                        ) : fieldType === 'select' && fieldOptions.length > 0 ? (
                          <select
                            value={String(filter.value || '')}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              if (!selectedValue) {
                                handleFilterChange(filter.id, 'value', '');
                                return;
                              }
                              const selectedOption = fieldOptions.find(opt => String(opt.value) === selectedValue);
                              if (selectedOption) {
                                // Preserve the original type (number or string)
                                handleFilterChange(filter.id, 'value', selectedOption.value);
                              } else {
                                // Fallback: try to convert to number if it looks like a number
                                const numValue = Number(selectedValue);
                                handleFilterChange(filter.id, 'value', isNaN(numValue) ? selectedValue : numValue);
                              }
                            }}
                            className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select...</option>
                            {fieldOptions.map((option) => (
                              <option key={String(option.value)} value={String(option.value)}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={String(filter.value || '')}
                            onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
                            placeholder={`Enter ${selectedField?.label.toLowerCase() || 'value'}...`}
                            className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        )}
                      </div>

                      <div className="flex items-end">
                        <button
                          onClick={() => handleRemoveFilter(filter.id)}
                          className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Remove filter"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Employee Table - Desktop */}
      <div className="hidden md:block bg-white border border-neutral-200 rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50">
              <tr className="border-b border-neutral-200">
                <th 
                  className="text-left py-3 px-4 text-sm font-medium text-neutral-700 cursor-pointer hover:bg-neutral-100 select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">
                    Name
                    {sortColumn === 'name' && (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="h-4 w-4 text-blue-600" />
                      ) : (
                        <ArrowDown className="h-4 w-4 text-blue-600" />
                      )
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Email</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Phone</th>
                <th 
                  className="text-left py-3 px-4 text-sm font-medium text-neutral-700 cursor-pointer hover:bg-neutral-100 select-none"
                  onClick={() => handleSort('position')}
                >
                  <div className="flex items-center gap-2">
                    Position
                    {sortColumn === 'position' && (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="h-4 w-4 text-blue-600" />
                      ) : (
                        <ArrowDown className="h-4 w-4 text-blue-600" />
                      )
                    )}
                  </div>
                </th>
                <th 
                  className="text-left py-3 px-4 text-sm font-medium text-neutral-700 cursor-pointer hover:bg-neutral-100 select-none"
                  onClick={() => handleSort('location')}
                >
                  <div className="flex items-center gap-2">
                    Location
                    {sortColumn === 'location' && (
                      sortDirection === 'asc' ? (
                        <ArrowUp className="h-4 w-4 text-blue-600" />
                      ) : (
                        <ArrowDown className="h-4 w-4 text-blue-600" />
                      )
                    )}
                  </div>
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-700">Status</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-neutral-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-8 px-4 text-center text-neutral-600">
                    {searchTerm || filters.length > 0 ? 'No employees found matching your filters.' : 'No employees found.'}
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
                    <td className="py-3 px-4 text-sm font-medium text-neutral-800">
                      {employee.firstName} {employee.lastName}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-600">
                      {employee.email}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-600">
                      {employee.phone || employee.phoneNumber || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {employee.profile?.mainPosition ? (
                        <div className="inline-flex items-center gap-1">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: employee.profile.mainPosition.color || '#3b82f6' }}>
                            {employee.profile.mainPosition.title}
                          </span>
                          {getSubPositions(employee).length > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowSubPositions(employee);
                              }}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                              title={`${getSubPositions(employee).length} additional position${getSubPositions(employee).length > 1 ? 's' : ''}`}
                            >
                              +{getSubPositions(employee).length}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-600">
                      <div className="inline-flex items-center gap-1">
                        <span>{employee.profile?.mainLocation?.name || employee.profile?.locationName || '-'}</span>
                        {getSubLocations(employee).length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowSubLocations(employee);
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                            title={`${getSubLocations(employee).length} additional location${getSubLocations(employee).length > 1 ? 's' : ''}`}
                          >
                            +{getSubLocations(employee).length}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {employee.isActive ? (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                      ) : (
                        <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => handleEdit(employee)}
                          className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Edit employee"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(employee.id)}
                          className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete employee"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Employee Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {filteredEmployees.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
            <User className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
            <p className="text-neutral-600">
              {searchTerm || filters.length > 0 ? 'No employees found matching your filters.' : 'No employees found.'}
            </p>
          </div>
        ) : (
          filteredEmployees.map((employee) => (
            <div key={employee.id} className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h3 className="font-medium text-neutral-900">
                    {employee.firstName} {employee.lastName}
                  </h3>
                  <p className="text-sm text-neutral-600 mt-1">{employee.email}</p>
                  {employee.phone || employee.phoneNumber ? (
                    <p className="text-sm text-neutral-600">{employee.phone || employee.phoneNumber}</p>
                  ) : null}
                </div>
                {employee.isActive ? (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>
                ) : (
                  <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded">Inactive</span>
                )}
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {employee.profile?.mainPosition && (
                  <div className="inline-flex items-center gap-1">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white"
                          style={{ backgroundColor: employee.profile.mainPosition.color || '#3b82f6' }}>
                      {employee.profile.mainPosition.title}
                    </span>
                    {getSubPositions(employee).length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowSubPositions(employee);
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                        title={`${getSubPositions(employee).length} additional position${getSubPositions(employee).length > 1 ? 's' : ''}`}
                      >
                        +{getSubPositions(employee).length}
                      </button>
                    )}
                  </div>
                )}
                {employee.profile?.mainLocation && (
                  <div className="inline-flex items-center gap-1">
                    <span className="text-xs text-neutral-600">
                      {employee.profile.mainLocation.name}
                    </span>
                    {getSubLocations(employee).length > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowSubLocations(employee);
                        }}
                        className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                        title={`${getSubLocations(employee).length} additional location${getSubLocations(employee).length > 1 ? 's' : ''}`}
                      >
                        +{getSubLocations(employee).length}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2 justify-end mt-3">
                <button
                  onClick={() => handleEdit(employee)}
                  className="p-2 text-neutral-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  title="Edit employee"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(employee.id)}
                  className="p-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  title="Delete employee"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Employee Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-neutral-800 mb-6">
              Add New Employee
            </h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm password"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  PIN (4 digits) *
                </label>
                <input
                  type="text"
                  required
                  maxLength={4}
                  value={formData.pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    setFormData({ ...formData, pin: value });
                  }}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter 4-digit PIN"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Main Location *
                  </label>
                  <select
                    required
                    value={formData.mainLocationId || ''}
                    onChange={(e) => setFormData({ ...formData, mainLocationId: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">Select Location</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Main Position *
                  </label>
                  <select
                    required
                    value={formData.mainPositionId || ''}
                    onChange={(e) => setFormData({ ...formData, mainPositionId: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">Select Position</option>
                    {positions.map((pos) => (
                      <option key={pos.id} value={pos.id}>
                        {pos.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Payroll Department *
                  </label>
                  <select
                    required
                    value={formData.payrollDepartmentId || ''}
                    onChange={(e) => setFormData({ ...formData, payrollDepartmentId: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">Select Payroll Department</option>
                    {payrollDepartments.filter(dept => dept.isActive).map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Employment Type *
                  </label>
                  <select
                    required
                    value={formData.employmentType || ''}
                    onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Employment Type</option>
                    <option value="Permanent">Permanent</option>
                    <option value="Contract">Contract</option>
                    <option value="Agency">Agency</option>
                  </select>
                </div>
              </div>

              <BasketSelect
                label="App Roles"
                required
                availableItems={roles.map(role => ({ id: role.id, label: role.name }))}
                selectedIds={formData.appRoleIds}
                onChange={(ids) => setFormData({ ...formData, appRoleIds: ids.map(id => typeof id === 'string' ? parseInt(id) : id) })}
                placeholder="Select roles..."
              />

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setError(null);
                  }}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Create Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sub-Locations Dialog */}
      {showSubLocationsDialog && selectedEmployeeForSubLocations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                Additional Locations for {selectedEmployeeForSubLocations?.firstName} {selectedEmployeeForSubLocations?.lastName}
              </h3>
              <button
                onClick={() => {
                  setShowSubLocationsDialog(false);
                  setSelectedEmployeeForSubLocations(null);
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {selectedEmployeeForSubLocations && getSubLocations(selectedEmployeeForSubLocations).length === 0 ? (
                <p className="text-neutral-500 text-center py-4">No additional locations</p>
              ) : selectedEmployeeForSubLocations ? (
                <ul className="space-y-2">
                  {getSubLocations(selectedEmployeeForSubLocations).map((location) => (
                    <li key={location.id} className="px-3 py-2 bg-neutral-50 rounded-md text-sm text-neutral-700">
                      {location.name}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setShowSubLocationsDialog(false);
                  setSelectedEmployeeForSubLocations(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Positions Dialog */}
      {showSubPositionsDialog && selectedEmployeeForSubPositions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                Additional Positions for {selectedEmployeeForSubPositions?.firstName} {selectedEmployeeForSubPositions?.lastName}
              </h3>
              <button
                onClick={() => {
                  setShowSubPositionsDialog(false);
                  setSelectedEmployeeForSubPositions(null);
                }}
                className="text-neutral-400 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {selectedEmployeeForSubPositions && getSubPositions(selectedEmployeeForSubPositions).length === 0 ? (
                <p className="text-neutral-500 text-center py-4">No additional positions</p>
              ) : selectedEmployeeForSubPositions ? (
                <ul className="space-y-2">
                  {getSubPositions(selectedEmployeeForSubPositions).map((position) => (
                    <li key={position.id} className="px-3 py-2 rounded-md text-sm font-medium text-white"
                        style={{ backgroundColor: position.color || '#3b82f6' }}>
                      {position.title}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setShowSubPositionsDialog(false);
                  setSelectedEmployeeForSubPositions(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeList;
